<template>
  <div class="w-full h-full justify-center text-center">
    <div class="pt-10">
      <div class="mb-3">
        <LayupIcon class="my-3" />
        <h3 class="text-lg font-semibold text-black">Enter your email.</h3>
        <p v-if="errorMessage" class="text-red-700">{{ errorMessage }}</p>
      </div>

      <div v-if="isLoading">
        <LayupSpinner />
      </div>
      <form class="m-0 p-0" v-else>
        <UsernameInput
          v-model:username="email"
          v-model:is-username-valid="isUsernameValid"
          :label="'Email'"
          :disabled="isLoading"
          class="cellnumber text-left"
          @error="
            (error) => {
              errorMessage = error;
            }
          "
          :placeholder="'Enter your email'"
        />
        <LayupButton
          type="submit"
          :disabled="isLoading || !isUsernameValid"
          :isLoading="isLoading"
          :label="'Save'"
          @click="onSubmit()"
        />
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import LayupButton from '@/components/common/LayupButton.vue';
import LayupSpinner from '@/components/common/LayupSpinner.vue';
import UsernameInput from '@/components/common/UsernameInput.vue';
import LayupIcon from '@/components/icons/LayupIcon.vue';
import { NetworkError } from '@/errors';
import { updateUser } from '@/services/user';
import { useUserStore } from '@/stores/users';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import { ref } from 'vue';
import type { UserServiceApiUserServiceUpdateRequest } from '@layup/api-sdk';
import { useBoostStoreModal } from './useBoostStoreModal';

const { goToStep } = useBoostStoreModal();
const userStore = useUserStore();

const isUsernameValid = ref(false);
const errorMessage = ref('');
const email = ref('');
const isLoading = ref(false);

const onSubmit = async () => {
  try {
    isLoading.value = true;

    const payload: UserServiceApiUserServiceUpdateRequest = {
      id: userStore.user._id || '',
      modelUserRequest: {
        email: email.value,
      },
      isRealtimeVerification: true,
    };

    const response = await updateUser(payload);
    const { _id, name, verifiedEmail, cellNumber, verifiedMobile } = response;
    userStore.user._id = _id;
    userStore.user.name = name;
    userStore.user.email = response.email;
    userStore.user.verifiedEmail = verifiedEmail!;
    userStore.user.verifiedMobile = verifiedMobile!;
    userStore.user.cellNumber = cellNumber!;
    addMetric('enter_email', { status: 'success', channel: 'boost-store-modal' });
    goToStep('contribute');
  } catch (error: any) {
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('enter_email', { status: 'network_error', channel: 'boost-store-modal' });
      errorMessage.value = 'Network Error. Please try again.';
    } else {
      createException(error);
      addMetric('enter_email', { status: 'error', channel: 'boost-store-modal' });
      if (error?.response?.data) {
        errorMessage.value = error.response.data;
      } else {
        errorMessage.value = 'An unexpected error occurred. Please try again.';
      }
    }
  } finally {
    isLoading.value = false;
  }
};
</script>
